// src/pages/Admin/ActasAdmin.js

import React, { useState, useMemo } from "react";
import { HeaderPage } from "../../components/Admin";
import { ActasTable } from "../../components/Admin/ActasTable";
import { useAuth } from "../../hooks/useAuth";
import { useActas } from "../../hooks/useActas";
import { generarActaApi } from "../../api/meetingSessionActa";
import { generarActaAutoApi } from "../../api/meetings";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../scss/ActasAdmin.scss";

export function ActasAdmin() {
  const { auth } = useAuth();
  const { sessions, loading, error, reload } = useActas();
  const [loadingSessionId, setLoadingSessionId] = useState(null);
  const [loadingMeetingId, setLoadingMeetingId] = useState(null);
  const navigate = useNavigate();

  // Agrupar sesiones por reunión (para la tabla)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of sessions || []) {
      const meetingId = s.meeting;
      if (!map.has(meetingId)) {
        map.set(meetingId, {
          meetingId,
          meetingTitle: s.meeting_title,
          ciudad: s.ciudad,
          centro: s.centro,
          sessions: [],
        });
      }
      map.get(meetingId).sessions.push(s);
    }
    // Orden opcional por fecha de la primera sesión (más reciente primero)
    return Array.from(map.values()).sort((a, b) => {
      const aDate = a.sessions[0]?.start_datetime || "";
      const bDate = b.sessions[0]?.start_datetime || "";
      return new Date(bDate) - new Date(aDate);
    });
  }, [sessions]);

  // Generar acta por sesión (flujo existente)
  const handleGenerateActa = async (session) => {
    if (!window.confirm("¿Confirmas generar el acta de la sesión?")) return;
    setLoadingSessionId(session.id);
    try {
      await generarActaApi(session.id, auth.token);
      toast.success("¡Acta de sesión generada!");
      await reload();
    } catch {
      toast.error("No se pudo generar el acta de sesión");
    } finally {
      setLoadingSessionId(null);
    }
  };

  // Generación unificada (auto) a nivel reunión
  const handleGenerateActaAuto = async (meetingId) => {
    if (!window.confirm("¿Generar el ACTA (Auto) para esta reunión?")) return;
    setLoadingMeetingId(meetingId);
    try {
      const res = await generarActaAutoApi(meetingId, auth.token);
      // res.type: "session" | "consolidated"
      toast.success(
        res?.type === "session"
          ? "¡Acta de sesión generada!"
          : "¡Acta consolidada generada!"
      );

      // (opcional) abrir preview HTML ligero en nueva pestaña
      if (res?.type === "session" && res?.session_id) {
        navigate(`/app/actas/${res.session_id}/editar`);
      } else if (res?.type === "consolidated") {
        navigate(`/app/actas/consolidado/${meetingId}`, {
          state: { preview: res.preview, docx_url: res.docx_url },
        });
      }

      // (opcional) descarga DOCX
      // if (res?.docx_url) window.open(res.docx_url, "_blank");

      await reload();
    } catch (e) {
      toast.error(e?.message || "No se pudo generar el acta (auto)");
    } finally {
      setLoadingMeetingId(null);
    }
  };

  // Vista previa/edición de la sesión
  const handlePreviewActa = (session) => {
    navigate(`/app/actas/${session.id}/editar`);
  };

  if (error) return <div>Error cargando sesiones.</div>;

  return (
    <>
      <HeaderPage title="🧠 Actas inteligentes" />
      <ActasTable
        groupedMeetings={grouped}
        loading={loading}
        loadingSessionId={loadingSessionId}
        loadingMeetingId={loadingMeetingId}
        onGenerateActaSession={handleGenerateActa}
        onPreviewActaSession={handlePreviewActa}
        onGenerateActaAuto={handleGenerateActaAuto}
      />
    </>
  );
}
